/* eslint-disable @typescript-eslint/camelcase */
import axios from "axios";
import uuid from "uuid";
import querystring from "querystring";

const instance = axios.create({
  baseURL:
    "https://4xkw82rc2l.execute-api.us-east-1.amazonaws.com/prod/facebook"
});

instance.interceptors.response.use(res => {
  return res.data;
});

const queryNestedEntity = (entName: string) => (fields: string[]) => {
  return `${entName}{${fields.join(",")}}`;
};
const compaigns = queryNestedEntity("");
const addSets = queryNestedEntity("adsets");
const adCreative = queryNestedEntity("ad_creative");
const ad = queryNestedEntity("ad");

const setAuthToken = (token: string) => {
  instance.defaults.headers["Authorization"] = `Bearer ${token}`;
};

const accounts = async () => {
  return await instance.get("/me/adaccounts", {
    params: {
      fields: "id,account_id,name"
    }
  });
};

const getAds = (accountId: string) => async (videoName: string) => {
  const filtering = [
    {
      field: "name",
      operator: "CONTAIN",
      value: videoName
    }
  ];

  return await instance.get(`/${accountId}/ads`, {
    params: {
      filtering: JSON.stringify(filtering)
    }
  });
};

const getCampaigns = async (accountId: string, name?: string) => {
  const companyFields = "name,bid_strategy,daily_budget,objective,status";
  const filtering = [];
  if (name) {
    filtering.push({
      field: "name",
      operator: "CONTAIN",
      value: name
    });
  }
  return await instance.get(`/${accountId}/campaigns`, {
    params: {
      filtering: JSON.stringify(filtering),
      fields: [
        companyFields,
        addSets([
          "name",
          "attribution_spec",
          "bid_amount",
          "billing_event",
          "device_platforms",
          "optimization_goal",
          "promoted_object",
          "status",
          "targeting",
          "targeting_optimization",
          "user_device",
          "ads{name,creative{object_story_spec,object_type,status,asset_feed_spec}}" //asset_feed_spec
        ]),
        adCreative(["object_story_spec", "video_data"])
      ].join(",")
    }
  });
};

const getCreatives = async (accountId: string) => {
  return instance.get(`/${accountId}/adcreatives`, {
    params: {
      fields: "object_story_spec,name,title"
    }
  });
};

const getImages = async (accountId: string, name: string) => {
  return instance.get(`/${accountId}/adimages`, {
    params: {
      // filtering: JSON.stringify([
      //   { field: "name", operator: "CONTAIN", value: "untitled" }
      // ]),
      name,
      fields: "url,name,hash,created_time,account_id,id,title,permalink_url"
    }
  });
};
const getAudiences = async (accountId: string, name?: string) => {
  const filtering = [];
  if (name) {
    filtering.push({
      field: "name",
      operator: "CONTAIN",
      value: name
    });
  }
  return instance.get(`/${accountId}/customaudiences`, {
    params: {
      filtering: JSON.stringify(filtering),
      fields: "name,delivery_status"
    }
  });
};

const getAdvideos = async (accountId: string, title?: string) => {
  return instance.get(`/${accountId}/advideos`, {
    params: {
      title: title || undefined,
      fields: "source,title,thumbnails"
    }
  });
};

const campaignRequest = (
  accountId: string,
  name = "campaign",
  campaign: any
) => ({
  name,
  relative_url: `${accountId}/campaigns`,
  body: querystring.stringify(campaign)
});

const adSetRequest = (
  accountId: string,
  name = "adset",
  { promoted_object, targeting, attribution_spec = [], ...ad_set }: any,
  campaignRequestName: string
) => ({
  name,
  relative_url: `${accountId}/adsets`,
  body: querystring.stringify({
    campaign_id: campaignRequestName
      ? `{result=${campaignRequestName}:$.id}`
      : undefined,
    promoted_object: JSON.stringify({
      ...promoted_object
    }),
    targeting: JSON.stringify({
      ...targeting
    }),
    attribution_spec: JSON.stringify([...attribution_spec]),
    ...ad_set
  })
});

const adRequest = (
  accountId: string,
  name = "ad",
  { creative, ...ad }: any,
  adSetRequestName: string
) => {
  return {
    name,
    relative_url: `${accountId}/ads`,
    body: querystring.stringify({
      ...ad,
      adset_id: adSetRequestName
        ? `{result=${adSetRequestName}:$.id}`
        : undefined,
      creative: JSON.stringify(creative)
    })
  };
};

const createCampaign = async (
  accountId: string,
  [campaign, adset, ads]: any[]
) => {
  const campaignRequestName = uuid();
  const adSetRequestName = uuid();
  // chtobi zapuskat' LC eto pole nujno udalit'
  if (campaign.bid_strategy === "LOWEST_COST_WITHOUT_CAP")
    delete adset.bid_amount;

  return instance.post(`/${accountId}/async_batch_requests`, {
    name: uuid(),
    adbatch: [
      campaignRequest(accountId, campaignRequestName, campaign),
      adSetRequest(accountId, adSetRequestName, adset, campaignRequestName),
      ...ads.map((ad: any) =>
        adRequest(accountId, uuid(), ad, adSetRequestName)
      )
    ]
  });
};

const createAudience = async (accountId: string, audience: any) => {
  return instance.post(`/${accountId}/customaudiences`, audience);
};

const deleteAd = async (accountsId: string, adId: string) => {
  return await instance.delete(`/${accountsId}/advideos`, {
    params: {
      video_id: adId
    }
  });
};

const asyncRequestRequests = (asyncRequestId: string) =>
  instance.get(`/${asyncRequestId}/requests`, {
    params: {
      fields: "id,scope_object_id,status,result,input"
    }
  });

const asyncRequests = (
  accountId: string,
  since: number = new Date().getTime()
) =>
  instance
    .get(`/${accountId}/async_requests`, {
      params: {
        since,
        fields: "id"
      }
    })
    .then(({ data }) => {
      return Promise.all(
        data
          .slice(-10)
          .map(({ id }: { id: string }) => asyncRequestRequests(id))
      );
    })
    .then(requests => requests.map<any[]>(({ data }: any) => [...data]));

export default {
  accounts,
  setAuthToken,
  getCampaigns,
  getCreatives,
  getAdvideos,
  createCampaign,
  asyncRequests,
  getAudiences,
  createAudience,
  getAds,
  deleteAd,
  getImages
};
